import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyPanel } from "~/components/empty/empty-panel";
import { Icon } from "~/components/icon/icon";
import { InfoTooltip } from "~/components/info-tooltip";
import LoadingSpinner from "~/components/loading/loading-spinner";
import { Text } from "~/components/text";
import { cn } from "~/utils";
import {
  BacklistMetadataGapCard,
  BacklistMetadataGapCardProps,
} from "./backlist-metadata-gap-card";
import { useMetadataGapCounts } from "./use-metadata-gap-counts";

// Delay so counts that settle quickly never flash the loader.
const LOADER_DELAY_MS = 2000;

const panelClasses = {
  container:
    "gap-4 rounded-xl border border-secondary-50 bg-white px-6 py-12 shadow-xs",
  text: "max-w-lg",
};

type MetadataGapCardDefinition = Omit<
  BacklistMetadataGapCardProps,
  "threshold" | "count" | "subtitle"
> & { subtitle: (threshold: number) => string };

export const BacklistMetadataGaps = () => {
  const { t } = useTranslation(["home"]);

  const cards: MetadataGapCardDefinition[] = [
    {
      filterName: "keywordCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.keywords", { count: threshold }),
      iconName: "square-stack",
      iconWrapperClassName: "bg-[#FDF0F6]",
      iconClassName: "text-[#F32C69]",
    },
    {
      filterName: "descriptionWordCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.descriptionWords", {
          count: threshold,
        }),
      iconName: "text-align-start",
      iconWrapperClassName: "bg-primary-50",
      iconClassName: "text-primary-600",
    },
    {
      filterName: "themaCodeCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.themaCodes", { count: threshold }),
      iconName: "tag",
      iconWrapperClassName: "bg-[#F3EEFD]",
      iconClassName: "text-[#7A3FE4]",
    },
    {
      filterName: "bisacCodeCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.bisacCodes", { count: threshold }),
      iconName: "library",
      iconWrapperClassName: "bg-[#FEF1E6]",
      iconClassName: "text-[#E8701A]",
    },
  ];
  const { isLoading, cardsWithGaps, isAllCaughtUp } =
    useMetadataGapCounts(cards);

  const [isLoaderDue, setIsLoaderDue] = useState(false);
  useEffect(() => {
    if (!isLoading) return;
    const timeout = setTimeout(() => setIsLoaderDue(true), LOADER_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      setIsLoaderDue(false);
    };
  }, [isLoading]);

  return (
    <div>
      <div className="space-y-1">
        <Text as="h2" variant="heading">
          {t("home:metadataGaps.title")}
        </Text>
        <div className="flex items-center gap-1">
          <Text variant="muted">{t("home:metadataGaps.description")}</Text>
          <InfoTooltip
            label={t("home:metadataGaps.tooltipLabel")}
            text={t("home:metadataGaps.tooltip")}
            iconSize="small"
            side="right"
          />
        </div>
      </div>

      <div role="status">
        {isLoading && (
          <div
            className={cn("mt-4", !isLoaderDue && "invisible")}
            aria-hidden={!isLoaderDue || undefined}
          >
            <EmptyPanel
              image={
                <div className="flex size-12 items-center justify-center rounded-full bg-primary-50">
                  <LoadingSpinner size="xs" />
                </div>
              }
              title={t("home:metadataGaps.loading.title")}
              text={t("home:metadataGaps.loading.text")}
              classes={panelClasses}
            />
          </div>
        )}
        {isAllCaughtUp && (
          <div className="mt-4">
            <EmptyPanel
              image={
                <div className="flex size-12 items-center justify-center rounded-full bg-icon-success-background">
                  <Icon
                    name="party-popper"
                    iconClassName="w-5! h-5! text-system-success"
                  />
                </div>
              }
              title={t("home:metadataGaps.allCaughtUp.title")}
              text={t("home:metadataGaps.allCaughtUp.text")}
              classes={panelClasses}
            />
          </div>
        )}
      </div>
      {!isLoading && cardsWithGaps.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cardsWithGaps.map(({ subtitle, ...card }) => (
            <BacklistMetadataGapCard
              key={card.filterName}
              {...card}
              subtitle={subtitle(card.threshold)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
